import * as React from "react"

import { cn } from "@/lib/utils"
import {
  applySanitizedValueToChangeEvent,
  sanitizeInputValue,
  shouldSanitizeInputType,
} from "@/lib/sanitize-input"

type InputProps = React.ComponentProps<"input"> & {
  /** Sanitize user input on change. Defaults to true. */
  sanitize?: boolean
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, onChange, sanitize = true, maxLength, ...props }, ref) => {
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!onChange) {
        return
      }

      if (!sanitize || shouldSanitizeInputType(type) === "none") {
        onChange(event)
        return
      }

      const sanitizedValue = sanitizeInputValue(event.target.value, type, {
        maxLength: maxLength ?? undefined,
      })

      onChange(applySanitizedValueToChangeEvent(event, sanitizedValue))
    }

    return (
      <input
        ref={ref}
        type={type}
        data-slot="input"
        maxLength={maxLength}
        className={cn(
          "file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground bg-card hover:bg-foreground/10 border-input flex h-9 w-full min-w-0 rounded-lg border px-3 py-1 text-xs shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
          "aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
          className
        )}
        onChange={handleChange}
        {...props}
      />
    )
  }
)

Input.displayName = "Input"

export { Input }
